import "./styles/rich-text-editor.css";

import {t} from "@lingui/core/macro";
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import {useId, useState} from "react";
import {type Editor} from "slate";
import {Editable, ReactEditor, Slate} from "slate-react";

import {View, type PropsFor} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {border, color, font, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {getBlockType} from "./slate/helpers/block-helpers.ts";
import {markdownStringToSlateElements} from "./slate/markdown-to-slate.ts";
import {
    renderElement,
    renderLeaf,
    renderPlaceholder,
} from "./slate/slate-renderers.tsx";
import {descendantsToMarkdown} from "./slate/slate-to-markdown.ts";
import {Toolbar} from "./toolbar.tsx";
import type {RichTextValidationError} from "./types.ts";
import {removeTrailingNewlines} from "./utils.ts";

import warningCircleIcon from "@phosphor-icons/core/bold/warning-circle-bold.svg";

export interface Props {
    disabled?: boolean;
    editor: Editor;
    error?: boolean;
    id: string;
    initialValue: string;
    label: string;
    onChange?: () => void;
    placeholder?: string;
    stickyToolbar?: boolean;
    style?: CSSProperties;
    validate: (text: string) => string | null | void;
    validationStrategy?: "CHANGE" | "BLUR" | "POSITIVE";
}

type SlateProps = PropsFor<typeof Slate>;
type EditableProps = PropsFor<typeof Editable>;

const renderEditableElement: EditableProps["renderElement"] = (elementProps) =>
    renderElement({
        ...elementProps,
        showParagraphWarnings: false,
        isDebugModeActive: true,
    });

const renderEditableLeaf: EditableProps["renderLeaf"] = (leafProps) =>
    renderLeaf({
        ...leafProps,
        showParagraphWarnings: false,
    });

// NOTE: If making major changes to this component, ensure all mocks are
// updated. Search for `RichTextEditorV2` in test files.
export default function RichTextEditorV2({
    disabled,
    editor,
    error,
    id,
    initialValue,
    label,
    onChange,
    placeholder,
    stickyToolbar,
    style,
    validate,
    validationStrategy = "BLUR",
}: Props) {
    const [errorMessage, setErrorMessage] = useState<string>("");

    const baseId = useId();
    const ids = {
        error: `${baseId}-error`,
        editor: `${baseId}-editor`,
        label: `${baseId}-label`,
    };

    const handleLabelClick = () => {
        ReactEditor.focus(editor);
    };

    const _validate = (text: string) => {
        setErrorMessage(validate(text) || "");
    };

    const handleChange: SlateProps["onChange"] = (editorChildren) => {
        if (
            validationStrategy === "CHANGE" ||
            (validationStrategy === "POSITIVE" && errorMessage)
        ) {
            const {result: updatedText} = descendantsToMarkdown(editorChildren);

            _validate(updatedText);
        }

        onChange?.();
    };

    const isSingleParagraph =
        editor.children.length === 1 && getBlockType(editor) === "paragraph";

    return (
        <View style={[styles.container, style]}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- HTML labels cannot link to [contenteditable] element with for. This replaces that behavior for some users. */}
            <LabelLarge
                htmlFor={id}
                id={ids.label}
                onClick={handleLabelClick}
                tag="label"
            >
                {label}
            </LabelLarge>
            <div>
                <Slate
                    editor={editor}
                    initialValue={
                        initialValue
                            ? (markdownStringToSlateElements(
                                  removeTrailingNewlines(initialValue),
                              ) as any)
                            : [
                                  {
                                      type: "paragraph",
                                      children: [{text: ""}],
                                  },
                              ]
                    }
                    onChange={handleChange}
                >
                    <Toolbar
                        disabled={disabled}
                        disableStickyToolbar={!stickyToolbar}
                    />

                    <Editable
                        aria-describedby={errorMessage ? ids.error : undefined}
                        aria-disabled={disabled ? "true" : undefined}
                        aria-invalid={
                            error || errorMessage ? "true" : undefined
                        }
                        aria-labelledby={ids.label}
                        className={`rich-text-editor ${css(
                            styles.instructionsTextarea,
                            (error || errorMessage) &&
                                styles.instructionsTextareaError,
                        )}`}
                        id={id}
                        onBlur={() =>
                            _validate(
                                descendantsToMarkdown(editor.children).result,
                            )
                        }
                        placeholder={
                            // Slate handles placeholders automatically for
                            // paragraphs but has issues with formatted blocks
                            // like lists. If the editor is a single paragraph,
                            // pass the placeholder normally. Otherwise remove
                            // it to avoid Slate's strange behavior.
                            editor.children.length === 0 || isSingleParagraph
                                ? placeholder
                                : undefined
                        }
                        // TODO: Consider using aria-disabled instead
                        readOnly={disabled}
                        renderElement={renderEditableElement}
                        renderLeaf={renderEditableLeaf}
                        renderPlaceholder={renderPlaceholder}
                    />
                </Slate>
            </div>
            {errorMessage ? (
                <LabelSmall
                    aria-atomic="true"
                    aria-live="assertive"
                    id={ids.error}
                    style={styles.errorText}
                >
                    <PhosphorIcon
                        aria-label={t`Error:`}
                        icon={warningCircleIcon}
                        size="small"
                    />
                    {errorMessage}
                </LabelSmall>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: spacing.small_12,
    },
    errorText: {
        display: "flex",
        alignItems: "center",
        color: color.red,
        gap: spacing.xSmall_8,
    },
    instructionsTextarea: {
        // NOTE: Without these properties, the font was ever-so-slightly bolder
        // than other fields. Definitely not a big deal, but this fixes it.
        "-moz-osx-font-smoothing": "grayscale",
        "-webkit-font-smoothing": "antialiased",
        background: color.white,
        border: `${border.width.thin} solid ${color.offBlack50}`,
        borderBlockStart: "none",
        borderRadius: `0 0 4px 4px`,
        color: color.offBlack,
        fontFamily: font.family.sans,
        fontSize: font.size.medium,
        height: 130,
        outlineColor: color.blue, // Matches SingleSelect
        overflowY: "auto",
        paddingBlock: 10, // Matches SingleSelect
        paddingInline: spacing.medium_16, // Matches SingleSelect
        resize: "vertical",
    },
    instructionsTextareaError: {
        background: color.fadedRed8,
        borderColor: color.red,
    },
});

export type {RichTextValidationError};
